/**
 * This file is pretty much just an obnoxious amount of geometry for the sake of placing
 * images inside grids with some amount of randomization.
 *
 * There is (probably) no simplification.
 */

const _ = require("lodash");
const RandomUtil = require("./RandomUtil");

/**
 * Generates a grouped image grid where each image type is in its own section.
 * Grid dimension calculation is slightly more complicated than generateImageGrid
 * since we don't want image groups to share columns.
 * Positioning is similar to generateImageGrid, but images are constrained to
 * stay closer to their groups.  Images in smaller groups will also trend larger.
 */
const generateGroupedImageGrid = function(
	imageBatex,
	imageCounts,
	aspectRatio,
	options = {}
) {
	const {minScale = 0.5, maxScale = 0.9} = options;
	const gridInfo = {aspectRatio};
	const {rows, cols} = calcGroupedGridDimensions(imageCounts, aspectRatio);

	// Generate seeded random for consistent layouts
	const hashInput = imageBatex.reduce(
		(a, batex, i) => a + `${batex}${imageCounts[i]}`,
		""
	);
	const seededRand = RandomUtil.pseudoRNG(hashInput);

	// Get the largest group size to determine the minimum scale
	const maxGroupSize = Math.max(...imageCounts);

	// Cell dimension calculations
	const cellWidth = 1 / cols;
	const cellHeight = 1 / rows;
	const cellAspectRatio = cellWidth / (cellHeight / aspectRatio);
	const maxImageSize = Math.min(1, 1 / cellAspectRatio);

	// Margin to keep images away from group edges (as fraction of cell width)
	const groupEdgeMargin = 0.15;

	gridInfo.labelWidth = 0.25;

	const imageGrid = [];
	let currentCol = 0;

	for (let typeIndex = 0; typeIndex < imageBatex.length; typeIndex++) {
		const batex = imageBatex[typeIndex];
		const count = imageCounts[typeIndex];
		const colsForType = Math.ceil(count / rows);

		// Groups that are smaller than the largest group get a higher minimum scale
		const groupScale = count / maxGroupSize;
		// Boost minScale for smaller groups: higher groupScale -> lower minScale
		// scaled exponentially because I think it's better to exaggerate the difference between
		// small and large groups
		const adjustedMinScale =
			minScale + (1 - Math.pow(groupScale, 2)) * (maxScale - minScale);

		const groupStartCol = currentCol;
		const groupEndCol = currentCol + colsForType - 1;

		// Calculate grid layout info for centering the last row
		const totalRows = Math.ceil(count / colsForType);
		const imagesInLastRow = count % colsForType || colsForType;

		// Iterate over images and calculate their grid placement
		for (let imageIndex = 0; imageIndex < count; imageIndex++) {
			const row = Math.floor(imageIndex / colsForType);
			const indexInRow = imageIndex % colsForType;

			// For the last row, center images horizontally (leaning right-heavy)
			const isLastRow = row === totalRows - 1;
			const blanks = colsForType - imagesInLastRow;
			const startCol = isLastRow ? Math.floor(blanks / 2) : 0;

			const col = currentCol + startCol + indexInRow;
			const imageData = {batex, row, col};

			// Constrain horizontal offset by group boundaries
			let minLeft = 0;
			let maxLeft = 1;

			const isLeftEdgeOfGroup = col === groupStartCol;
			const isRightEdgeOfGroup = col === groupEndCol;
			if (isLeftEdgeOfGroup) {
				minLeft = groupEdgeMargin;
			}
			if (isRightEdgeOfGroup) {
				maxLeft = 1 - groupEdgeMargin;
			}

			const layout = generateCellLayout(seededRand, {
				maxImageSize,
				minScale: adjustedMinScale,
				maxScale,
				cellAspectRatio,
				horizontalOffset: {
					min: minLeft,
					max: maxLeft,
				},
			});
			Object.assign(imageData, layout);

			imageGrid.push(imageData);
		}
		currentCol += colsForType;
	}

	gridInfo.imageGrid = imageGrid;
	gridInfo.gridSize = {cols, rows};
	return gridInfo;
};

/**
 * Given a set of image batex strings and counts, generate the layout parameters for
 * each image in something vaguely resembling a grid.
 */
const generateImageGrid = function(
	imageBatex,
	imageCounts,
	aspectRatio,
	options = {}
) {
	const {minScale = 0.5, maxScale = 0.9} = options;
	const gridInfo = {aspectRatio};
	const imagesMetadata = _.map(imageBatex, (batex, i) => ({
		batex,
		count: imageCounts[i],
	}));
	/*
		Generate repeatable semi-random placements based on
		batex inputs and counts (so layouts are consistent)
	*/
	const hashInput = imagesMetadata.reduce(
		(a, b) => a + `${b.batex}${b.count}`,
		""
	);
	const seededRand = RandomUtil.pseudoRNG(hashInput);

	// divide drawing area into areas so we know there won't be
	// significant overlap
	const totalImgQty = imageCounts.reduce((a, b) => a + b, 0);

	// This calculation is because rows x columns ~ number of items
	// and columns / rows ~ aspect ratio
	const rowQty = Math.round(Math.sqrt(totalImgQty / gridInfo.aspectRatio));
	const colQty = Math.ceil(totalImgQty / rowQty);
	const spaces = rowQty * colQty;

	// Getter function for image type based on probability
	const imageDataPickList = [];
	_.each(imagesMetadata, data => {
		for (let i = 0; i < data.count; i++) {
			imageDataPickList.push({batex: data.batex});
		}
	});
	for (let empty = 0; empty < spaces - totalImgQty; empty++) {
		imageDataPickList.push(null);
	}
	const getRandomImageOrEmptySpace = function() {
		const index = Math.floor(seededRand() * imageDataPickList.length);
		return imageDataPickList.splice(index, 1)[0];
	};
	const cellWidth = 1 / colQty; // In terms of total width
	const cellHeight = 1 / rowQty; // In terms of total width
	const cellAspectRatio = cellWidth / (cellHeight / gridInfo.aspectRatio);
	const maxImageSize = Math.min(1, 1 / cellAspectRatio); // In terms of the cell width

	// Generate image data
	gridInfo.labelWidth = 0.25;

	const imageGrid = [];
	for (let row = 0; row < rowQty; row++) {
		imageGrid.push([]);
		for (let col = 0; col < colQty; col++) {
			imageGrid[row][col] = getRandomImageOrEmptySpace();
			const imageData = imageGrid[row][col];
			if (imageData) {
				imageData.row = row;
				imageData.col = col;

				const layout = generateCellLayout(seededRand, {
					maxImageSize,
					minScale,
					maxScale,
					cellAspectRatio,
				});
				Object.assign(imageData, layout);
			}
		}
	}

	// remove empty cells and flatten into a one dimensional array
	gridInfo.imageGrid = _.compact(_.flatten(imageGrid));
	gridInfo.gridSize = {cols: colQty, rows: rowQty};
	return gridInfo;
};

/**
 * Generate image positions arranged in concentric rings inside a circle.
 * Packing isn't optimal, but the aesthetics should be okay for the numbers we're
 * likely to use.
 *
 * The hardest piece of this is more or less finding a reasonable side-length (x) for the individual
 * images inside the circle.  The way we handle this is to treat only the bounded circle inside
 * the image boundaries as important (we treat them as circles of diameter x).
 *
 * From there we calculate how many rings we need to pack all the images into the circle in
 * two cases:
 * - Scenario 1: The center ring has a diameter of x (in this case, exactly one image can fit)
 * - Scenario 2: The center ring has a diameter of 2x (in this case, three images can fit)
 *
 * We then choose the scenario that requires the fewest rings (incidentally this should mostly
 * prevent cases where we have only one or two images in the center ring for the numbers we're
 * likely to use).
 *
 * After calculating the number of rings, this function places the images in the rings by
 * packing them into inner rings and distributing the remaining images evenly in the outermost ring.
 */
const generateRadialImageGrid = function(imageBatex, imageCount, options = {}) {
	if (imageCount === 0) {
		return {positions: [], numRings: 0};
	}

	// Generate seeded random based on input for consistent layouts
	const hashInput = `${imageBatex}${imageCount}`;
	const seededRand = RandomUtil.pseudoRNG(hashInput);

	const {
		minScale = 0.8,
		maxScale = 0.95,
		maxImageSize = 1.3,
		maxOffset = 0.1,
		maxAngleOffsetDegrees = 15,
		maxRadialOffset = 0.05,
	} = options;

	// Scenario 1 (1 center): capacity of n rings = 3n² - 3n + 1
	// Scenario 2 (3 center): capacity of n rings = 3n²
	//
	// numRingsD1 = minimum rings needed for Scenario 1: 3n² - 3n + 1 >= imageCount
	//   n >= (3 + sqrt(12*imageCount - 3)) / 6
	// numRingsD2 = minimum rings needed for Scenario 2: 3n² >= imageCount
	//   n >= sqrt(imageCount/3)
	const numRingsD1 = Math.ceil((3 + Math.sqrt(12 * imageCount - 3)) / 6);
	const numRingsD2 = Math.ceil(Math.sqrt(imageCount / 3));
	const hasCenterImage = numRingsD1 <= numRingsD2;

	const positions = [];

	const placementOptions = {
		cellAspectRatio: 1,
		minScale,
		maxScale,
		maxImageSize,
		maxOffset,
	};

	// Random angular offset range
	const maxAngleOffset = (maxAngleOffsetDegrees * Math.PI) / 180;

	const initialRingCount = hasCenterImage ? 1 : Math.min(3, imageCount);

	const ringCounts = [initialRingCount];
	const capacityOffset = hasCenterImage ? 0 : 3; // there's real math for this but we already know the results
	let remaining = imageCount - initialRingCount;
	let rings = 1;
	while (remaining > 0) {
		const maxInRing = 6 * rings + capacityOffset;
		const actualInRing = Math.min(maxInRing, remaining);
		ringCounts.push(actualInRing);
		remaining -= actualInRing;
		rings++;
	}

	// Place images in each ring using polar coordinates
	// r is in "ring units" where 1 = distance between rings
	const ringOffset = hasCenterImage ? 0 : 0.5;
	ringCounts.forEach((count, ringIndex) => {
		for (let i = 0; i < count; i++) {
			const angle =
				(2 * Math.PI * i) / count + (seededRand() - 0.5) * 2 * maxAngleOffset;
			const radialOffset = seededRand() * maxRadialOffset;
			let r = ringIndex + ringOffset;
			if (r > 0) {
				r += radialOffset;
			}
			const layout = generateCellLayout(seededRand, placementOptions);
			positions.push({r, angle, ...layout});
		}
	});

	return {positions, numRings: rings};
};

/*******************************************************************************
 * Helpers
 ******************************************************************************/

const generateImageBatex = function(innerBatex, options = {}) {
	const {
		flipHorizontal = false,
		flipVertical = false,
		invisibleContent,
	} = options;
	let inner = innerBatex;
	if (invisibleContent) {
		inner += `\\invisible{${invisibleContent}}`;
	}
	if (flipHorizontal || flipVertical) {
		let mirrorOptions = "";
		if (flipHorizontal) mirrorOptions += "[y]"; // across y axis
		if (flipVertical) mirrorOptions += "[x]"; // across x axis
		return `$\\mirror${mirrorOptions}{${inner}}$`;
	} else {
		return `$${inner}$`;
	}
};

/**
 * Generate randomized positioning parameters for placing an image and label in a cell
 */
const generateCellLayout = function(rngFn, options = {}) {
	const {
		maxImageSize = 1,
		minScale = 0.5,
		maxScale = 0.9,
		cellAspectRatio = 1,
		horizontalOffset = {
			min: 0,
			max: 1,
		},
		verticalOffset = {
			min: 0,
			max: 1,
		},
	} = options;

	// Random scale factor between minScale and maxScale
	const scaleFactor = rngFn() * (maxScale - minScale) + minScale;
	const size = maxImageSize * scaleFactor;

	// Random horizontal flip (0 or 1)
	const flipped = Math.floor(rngFn() * 2);

	// Random offsets within the cell
	// leftOffset: how far right the image starts (0 = left edge, 1-size = right edge)
	const leftRand =
		rngFn() * (horizontalOffset.max - horizontalOffset.min) +
		horizontalOffset.min;
	const leftOffset = leftRand * (1 - size);

	// topOffset: how far down the image starts, accounting for cell aspect ratio (in units of the cell width)
	const topRand =
		rngFn() * (verticalOffset.max - verticalOffset.min) + verticalOffset.min;
	const topOffset = topRand * (1 - size * cellAspectRatio);

	// generate label offsets based on image offsets
	const lineWidthMargin = 0.05;
	const overlap = 0.15;

	const onLeft = leftRand < 0.5;
	const onTop = topRand < 0.5;

	const labelLeftOffset = onLeft
		? Math.min(
				leftOffset + size - overlap - lineWidthMargin,
				0.75 - lineWidthMargin
		  )
		: Math.max(lineWidthMargin, leftOffset - 0.25 + overlap + lineWidthMargin);

	const labelTopOffset = onTop
		? Math.min(
				topOffset + (size - overlap - lineWidthMargin) * cellAspectRatio,
				(0.75 - lineWidthMargin) * cellAspectRatio
		  )
		: Math.max(
				lineWidthMargin * cellAspectRatio,
				topOffset - (0.25 - overlap - lineWidthMargin) * cellAspectRatio
		  );

	return {
		size,
		flipped,
		leftOffset,
		topOffset,
		leftRand,
		topRand,
		labelLeftOffset,
		labelTopOffset,
	};
};

/**
 * Calculates optimal grid dimensions for grouped mode in a super naive way. Given the numbers I'm
 * expecting we'll use as input, this at least shouldn't run away.
 *
 * Approach:
 * 1. Start with the same initial guess as generateImageGrid (i.e. sqrt(totalImages/targetAspectRatio)
 *    rounded down gets the number of rows needed to place the images in a grid with roughly the given
 *    aspect ratio, without any extra space)
 * 2. If the initial guess is within 0.01 of the target aspect ratio, or the initial guess is less than
 *    the target aspect ratio, return the initial guess.
 * 3. Otherwise, increase the row count until we find a crossover point where the aspect ratio is less
 *    than the target aspect ratio.
 * 4. Then return the grid that is closer to the target aspect ratio.
 *
 * NOTE: In theory, since we take the floor of the initial estimation, it should never result in a grid
 * with an aspect ratio less than the target aspect ratio, except when 1/totalImages < targetAspectRatio
 * since the number of columns needed to place the images in the grid can only be underestimated by the
 * calculation (i.e. either all images are divisible by the initial row count, or we need to add empty spaces).
 */
const calcGroupedGridDimensions = function(imageCounts, targetAspectRatio) {
	// given a number of rows, calculate the number of columns needed to place
	// the images in the grid
	const calculateGridInfo = rows => {
		const colsPerType = imageCounts.map(count => Math.ceil(count / rows));
		const cols = colsPerType.reduce((a, b) => a + b, 0);
		return {rows, cols, aspectRatio: cols / rows};
	};

	const totalImgQty = imageCounts.reduce((a, b) => a + b, 0);
	const {
		rows: initialRows,
		cols: initialCols,
		aspectRatio: initialAspectRatio,
	} = calculateGridInfo(
		Math.max(1, Math.floor(Math.sqrt(totalImgQty / targetAspectRatio)))
	);

	// if we don't have enough elements to reach the target aspect ratio (the 1 x totalImages grid
	// isn't wide enough), or in the not super likely case that the initial guess is within 0.01
	// of the target aspect ratio, just return the initial guess
	if (
		Math.abs(initialAspectRatio - targetAspectRatio) < 0.01 ||
		initialAspectRatio < targetAspectRatio
	) {
		return {rows: initialRows, cols: initialCols};
	}

	// Otherwise increase the row count until we find a crossover point where the aspect ratio
	// is less than the target aspect ratio.  Then return the grid that is closer to the target
	// aspect ratio.
	let rowCount = initialRows;
	let colCount = initialCols;
	let aspectRatio = initialAspectRatio;
	let {
		rows: nextRowCount,
		cols: nextColCount,
		aspectRatio: nextAspectRatio,
	} = calculateGridInfo(rowCount + 1);

	while (aspectRatio > targetAspectRatio) {
		rowCount = nextRowCount;
		colCount = nextColCount;
		aspectRatio = nextAspectRatio;
		({
			rows: nextRowCount,
			cols: nextColCount,
			aspectRatio: nextAspectRatio,
		} = calculateGridInfo(rowCount + 1));
	}

	const distanceFromTarget = Math.abs(aspectRatio - targetAspectRatio);
	const nextDistanceFromTarget = Math.abs(nextAspectRatio - targetAspectRatio);

	if (distanceFromTarget <= nextDistanceFromTarget) {
		return {rows: rowCount, cols: colCount};
	} else {
		return {rows: nextRowCount, cols: nextColCount};
	}
};

module.exports = {
	generateImageGrid,
	generateGroupedImageGrid,
	generateImageBatex,
	generateCellLayout,
	generateRadialImageGrid,
};
